import { Grid } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { DotSpinner } from "@smartrr/shared/customerPortal/components/DotSpinner";
import { ICustomerPortalTheme } from "@smartrr/shared/entities/CustomerPortalTheme";
import {
  IShopifyProductIdToShopifyVariantIdMap,
  getFilteredShopifyProductIdToShopifyVariantIdMap,
} from "@smartrr/shared/entities/Purchasable";
import { ISmartrrSellingPlanGroup } from "@smartrr/shared/entities/SellingPlanGroup";
import React, { useCallback, useEffect, useMemo } from "react";

import { useCleanedAndSortedPurchables } from "@customer-app/state/reducers/purchasables";
import { useSellingPlanGroups } from "@customer-app/state/reducers/sellingPlanGroups";
import {
  useSmartrrCustomerDispatch,
  useSmartrrCustomerSelector,
} from "@customer-app/state/typedCustomerReduxHooks";

import { AquariusHeader } from "./AquariusHeader";
import { AquariusInner } from "./AquariusInner";
import "../../fonts.css";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { StyledToast } from "./components/StyledToast";
import useHandleUnavailableItemsAndDeliveries from "./hooks/useHandleUnavailableItemsAndDeliveries";
import { useSmartrrTheme } from "./hooks/useSmartrrTheme";
import { OverrideCSS } from "./OverrideCSS";
import { useMobileQuery } from "./utils";

declare module "@mui/material/styles" {
  interface BreakpointOverrides {
    xs: true;
    sm: true;
    md: true;
    lg: true;
    xl: true;
    mdsm: true; // adds the `mobile` breakpoint
  }

  interface Theme extends ICustomerPortalTheme {
    breakPoints?: BreakpointOverrides;
  }

  interface TypographyVariants {
    caption1: React.CSSProperties;
    caption2: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    caption1?: React.CSSProperties;
    caption2?: React.CSSProperties;
  }
}
// Update the Typography's variant prop options
declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    //Not supporting default `caption` variant, replacing it with caption1 & caption2 for more flexibility
    caption1: true;
    caption2: true;
    // Unsupported ---
    subtitle1: false;
    subtitle2: false;
    caption: false;
    overline: false;
  }
}

const HandleLoading = () => {
  const isThemeLoading = useSmartrrCustomerSelector(state => state.theme.isLoading);
  const isPurchaseStateLoading = useSmartrrCustomerSelector(state => state.purchaseStates.isLoading);
  const isPurchasablesLoading = useSmartrrCustomerSelector(state => state.purchasables.isLoading);
  const isLoading = isThemeLoading || isPurchaseStateLoading || isPurchasablesLoading;

  const theme = useSmartrrTheme();

  return (
    <React.Fragment>
      {isLoading ? (
        <Grid
          container
          spacing={0}
          direction="column"
          alignItems="center"
          justifyContent="center"
          style={{
            position: "absolute",
            top: "0%",
            width: "100%",
            height: "100%",
            backgroundColor: theme.modernPageBackground ?? "white",
            zIndex: "1000",
          }}
          id="smartrr_main_loader"
        >
          <Grid item xs={3}>
            <DotSpinner />
          </Grid>
        </Grid>
      ) : (
        <React.Fragment></React.Fragment>
      )}
    </React.Fragment>
  );
};

const useHandleSellingPlanEnablement = () => {
  const { purchasables } = useCleanedAndSortedPurchables();
  const sellingPlanGroups = useSellingPlanGroups();
  const sellingPlanGroupsByProductId = useSmartrrCustomerSelector(
    state => state.sellingPlanGroups.sellingPlanGroupsByProductId
  );
  const sellingPlanGroupsByVariantId = useSmartrrCustomerSelector(
    state => state.sellingPlanGroups.sellingPlanGroupsByVariantId
  );
  const productSellingPlanGroupsEntries = Object.entries(sellingPlanGroupsByProductId);
  const variantSellingPlanGroupsEntries = Object.entries(sellingPlanGroupsByVariantId);

  return useMemo(() => {
    const sellingPlanGroupsWithEnablementMap: (ISmartrrSellingPlanGroup & {
      enablementMap: IShopifyProductIdToShopifyVariantIdMap;
    })[] = sellingPlanGroups.map(sellingPlanGroup => {
      const productIds = productSellingPlanGroupsEntries
        .filter(([, spgArr]) => spgArr.some(spg => spg.shopifyNumericId === sellingPlanGroup.shopifyNumericId))
        .map(([productId]) => productId);
      const variantIds = variantSellingPlanGroupsEntries
        .filter(([, spgArr]) => spgArr.some(spg => spg.shopifyNumericId === sellingPlanGroup.shopifyNumericId))
        .map(([variantId]) => variantId);

      return {
        ...sellingPlanGroup,
        enablementMap: getFilteredShopifyProductIdToShopifyVariantIdMap(purchasables, productIds, variantIds),
      };
    });

    return sellingPlanGroupsWithEnablementMap;
  }, [purchasables, sellingPlanGroups, sellingPlanGroupsByProductId, sellingPlanGroupsByVariantId]);
};

const HandleEnablementAndOOSSetup = () => {
  const dispatch = useSmartrrCustomerDispatch();

  const sellingPlanGroupsWithEnablementMap = useHandleSellingPlanEnablement();
  const unavailableVariantsAndDeliveries = useHandleUnavailableItemsAndDeliveries();

  setTimeout(() => {
    dispatch({
      type: "SELLING_PLAN_GROUPS_ENABLEMENT_MAP",
      payload: {
        sellingPlanGroupsWithEnablementMap,
      },
    });

    dispatch({
      type: "LOADED_UNAVAILABLE_VARIANTS_AND_DELIVERIES",
      payload: {
        unavailableVariantsAndDeliveries,
      },
    });
  }, 0);

  return <React.Fragment></React.Fragment>;
};

export function OperationAquarius(): JSX.Element {
  const isMobile = useMobileQuery();
  const overrideCSS = useSmartrrCustomerSelector(state => state.theme.theme.overrideCSS);
  const theme = useSmartrrTheme();

  const takeFullWidth = useCallback((ref: HTMLDivElement | null, resize = false) => {
    if (!ref) {
      return;
    }

    if (ref.closest("[data-smartrr-full-width]")) {
      if (resize) {
        ref.style.width = "100vw";
      }
      ref.style.marginLeft = `0`;
      ref.style.marginLeft = `${-ref.getBoundingClientRect().left}px`;
    }
  }, []);

  useEffect(() => {
    const autoResize = () => {
      const element: HTMLDivElement | null = document.querySelector("[data-smartrr-full-width] #smartrr_wrapper");

      if (element) {
        takeFullWidth(element);
      }
    };

    window.addEventListener("resize", autoResize);

    return () => window.removeEventListener("resize", autoResize);
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <OverrideCSS overrideCSS={overrideCSS} />
      <div
        style={{
          minHeight: "100vh",
          position: "relative",
        }}
        id="smartrr_wrapper"
        ref={ref => takeFullWidth(ref, true)}
      >
        <ErrorBoundary>
          <HandleLoading />
        </ErrorBoundary>
        <ErrorBoundary>
          <AquariusHeader />
        </ErrorBoundary>
        <ErrorBoundary>
          <AquariusInner />
        </ErrorBoundary>
        <ErrorBoundary>
          <HandleEnablementAndOOSSetup />
        </ErrorBoundary>
        <StyledToast isMobile={isMobile} />
      </div>
    </ThemeProvider>
  );
}
